import { createClient } from '@supabase/supabase-js';
import { memoryCache, CACHE_KEYS, CACHE_TTL } from './cache';
import { EnhancedCache } from './enhanced-cache';
import { logger, checkConfig } from './logger';

logger.info('Initializing Supabase client');

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Only log configuration status in development
logger.configCheck({
  supabaseUrl,
  supabaseAnonKey,
  mode: import.meta.env.MODE
});

if (!supabaseUrl || !supabaseAnonKey) {
  logger.error('Missing required environment variables for Supabase');

  // Show user-friendly error in production
  if (typeof window !== 'undefined') {
    const errorDiv = document.createElement('div');
    errorDiv.innerHTML = `
      <div style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: #1f2937; color: white; display: flex; align-items: center; justify-content: center; z-index: 9999; font-family: system-ui;">
        <div style="text-align: center; max-width: 500px; padding: 2rem;">
          <div style="font-size: 3rem; margin-bottom: 1rem;">⚠️</div>
          <h1 style="font-size: 1.5rem; margin-bottom: 1rem; color: #ef4444;">Configuration Error</h1>
          <p style="margin-bottom: 1rem; color: #9ca3af;">The application is missing required Supabase configuration.</p>
          <p style="font-size: 0.875rem; color: #6b7280;">Please ensure VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY are configured in your deployment environment.</p>
        </div>
      </div>
    `;
    document.body.appendChild(errorDiv);
  }

  throw new Error('Missing required Supabase environment variables');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true, // Enable session persistence to avoid rate limits
    autoRefreshToken: true, // Enable auto-refresh for anonymous sessions
    detectSessionInUrl: false, // Keep this false for anonymous auth
    flowType: 'implicit', // Use implicit flow for anonymous auth
    storage: window.localStorage, // Use localStorage to persist session across browser restarts
    debug: false // Disable debug logs to reduce console noise
  },
  global: {
    headers: {
      'X-Client-Info': 'pochoclo-tv@1.0.0',
    },
  },
  db: {
    schema: 'public',
  },
  realtime: {
    params: {
      eventsPerSecond: 2,
    },
  },
});

//console.log('✅ supabase.ts: Supabase client created successfully');

// Initialize anonymous authentication
let authInitialized = false;

export async function initializeAuth() {
  if (authInitialized) return;

  try {
    logger.info('🔐 Initializing anonymous authentication...');

    // Check if we already have a session
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();

    if (sessionError) {
      logger.error('🚨 Error getting session:', sessionError.message);

      // If it's a 403 error, the anon key might be invalid
      if (sessionError.message?.includes('403') || sessionError.message?.includes('Forbidden')) {
        logger.error('🚨 Authentication forbidden - check your Supabase anon key');
        throw new Error('Authentication configuration error. Please check your Supabase settings.');
      }
    }

    if (session && session.user) {
      const now = Math.floor(Date.now() / 1000);
      const expiresAt = session.expires_at || 0;
      const isExpired = expiresAt <= now;
      const isExpiringSoon = expiresAt <= (now + 300); // 5 minutes

      if (isExpired) {
        logger.info('🔄 Session expired, refreshing for existing user...');

        // Try to refresh the session
        const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();

        if (refreshError) {
          logger.error('🚨 Session refresh failed:', refreshError);

          // If refresh token is not found, clear the session and create new anonymous session
          if (refreshError.message?.includes('Refresh Token Not Found') || refreshError.message?.includes('refresh_token_not_found')) {
            logger.info('🧹 Clearing invalid session and creating new anonymous user...');
            await supabase.auth.signOut();
          }

          await createNewAnonymousSession();
        } else {
          logger.info('✅ Session refreshed successfully for user:', refreshData.user?.id);
        }
      } else if (isExpiringSoon) {
        logger.info('🔄 Session expiring soon, proactively refreshing...');

        // Proactively refresh session that's expiring soon
        const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();

        if (refreshError) {
          logger.warn('⚠️ Proactive refresh failed:', refreshError);

          // If refresh token is not found, create a new anonymous session
          if (refreshError.message?.includes('Refresh Token Not Found') || refreshError.message?.includes('refresh_token_not_found')) {
            logger.info('🔐 Refresh token not found, clearing session and creating new anonymous session...');
            await supabase.auth.signOut();
            await createNewAnonymousSession();
          }
        } else {
          logger.info('✅ Session proactively refreshed for user:', refreshData.user?.id);
        }
      } else {
        logger.info('✅ Valid session found for user:', session.user.id,
          '(expires in', Math.floor((expiresAt - now) / 60), 'minutes)');
      }
    } else {
      logger.info('🔐 No existing session found, creating new anonymous user...');
      await createNewAnonymousSession();
    }

    authInitialized = true;
  } catch (error) {
    logger.error('🚨 Failed to initialize anonymous authentication:', error);
    // Don't throw here - let the app continue without auth if needed
    authInitialized = true; // Mark as initialized even on failure to prevent retries
  }
}

// Helper function to create new anonymous session
async function createNewAnonymousSession() {
  try {
    logger.info('🔐 Creating new anonymous session...');

    const { data, error } = await supabase.auth.signInAnonymously();

    if (error) {
      logger.error('🚨 Anonymous sign-in failed:', error.message);

      // Provide more specific error messages
      if (error.message?.includes('403') || error.message?.includes('Forbidden')) {
        throw new Error('Authentication forbidden. Please check your Supabase configuration.');
      } else if (error.message?.includes('Invalid API key')) {
        throw new Error('Invalid Supabase API key. Please check your environment variables.');
      } else {
        throw new Error(`Authentication failed: ${error.message}`);
      }
    }

    logger.info('✅ New anonymous user created successfully:', data.user?.id);

    return data;
  } catch (error) {
    logger.error('🚨 Failed to create anonymous session:', error);
    throw error;
  }
}

// Helper function to get proper poster URL
export function getPosterUrl(posterPath: string): string {
  if (!posterPath) return '';

  // Already a full Supabase Storage or http(s) URL
  if (posterPath.includes('supabase.co/storage') || /^https?:\/\//.test(posterPath)) {
    return posterPath;
  }

  // TMDB path (/w500 etc)
  if (posterPath.startsWith('/')) {
    return `https://image.tmdb.org/t/p/w500${posterPath}`;
  }

  // Normalize storage path/filename to avoid posters/posters/* duplication
  const clean = posterPath.replace(/^\/+/, '');
  const pathWithinBucket = clean.replace(/^public\//, '').replace(/^posters\//, '');
  return `${supabaseUrl}/storage/v1/object/public/posters/${pathWithinBucket}`;
}

// Database types
export interface StreamingPlatform {
  id: string;
  name: string;
  icon_url: string;
  website_url: string;
  created_at: string;
}

export interface Content {
  id: string;
  original_title?: string;
  type: 'movie' | 'series';
  release_year: number;
  popularity_score: number;
  imdb_rating: number;
  genres: string[];
  created_at: string;
  source: 'manual' | 'tmdb';
  tmdb_id?: number;
  last_updated: string;
}

export interface ContentAvailability {
  id: string;
  content_id: string;
  country_content_id: string;
  platform_ids: string[];
  country_code: string;
  available_date: string;
  expires_date: string | null;
  created_at: string;
  external_id?: string;
  description: string;
  poster_url: string;
  localized_title: string;
  streaming_platforms?: StreamingPlatform[];
}

export interface ContentWithPlatforms extends Content {
  availability?: (ContentAvailability & {
    streaming_platforms: StreamingPlatform[];
  })[];
}

export interface UserPreferences {
  id: string;
  user_id: string;
  last_selected_country: string;
  created_at: string;
  updated_at: string;
}

// Cached query functions
export async function getCachedContent(country: string, limit: number = 20) {
  return EnhancedCache.get(
    CACHE_KEYS.CONTENT_LIST(country),
    async () => {
      //console.log('🔍 Fetching fresh content for', country, '- limit:', limit);

      // Get current and previous year
      const currentYear = new Date().getFullYear();
      const previousYear = currentYear - 1;

      const { data, error } = await supabase
        .from('content')
        .select(`
          *,
          availability:content_availability!inner(
            *,
            streaming_platforms(*)
          )
        `)
        .eq('availability.country_code', country)
        .in('release_year', [currentYear, previousYear])
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('🚨 Database query error:', error);
        throw error;
      }

      const result = data || [];
      console.log('🔍 Fresh query results:', {
        country,
        years: [currentYear, previousYear],
        resultCount: result.length,
      });

      return result;
    },
    CACHE_TTL.CONTENT_LIST,
    'stale-while-revalidate' // Use stale-while-revalidate for better UX
  );
}

export async function getCachedContentDetail(contentId: string) {
  return EnhancedCache.get(
    CACHE_KEYS.CONTENT_DETAIL(contentId),
    async () => {
      //console.log('🔍 Fetching fresh content detail for', contentId);
      const { data, error } = await supabase
        .from('content')
        .select('*')
        .eq('id', contentId)
        .single();

      if (error) throw error;
      return data;
    },
    CACHE_TTL.CONTENT_DETAIL,
    'cache-first' // Content details rarely change
  );
}

export async function getCachedAvailability(contentId: string, country: string) {
  return EnhancedCache.get(
    CACHE_KEYS.COUNTRY_AVAILABILITY(contentId, country),
    async () => {
      logger.info('🔍 Fetching fresh availability for', contentId, country);
      const { data, error } = await supabase
        .from('content_availability')
        .select(`
          *,
          streaming_platforms(*)
        `)
        .eq('content_id', contentId)
        .eq('country_code', country);

      if (error) throw error;

      // Process the data to ensure streaming_platforms are properly populated
      const processedData = await Promise.all(
        (data || []).map(async (avail) => {
          if (avail.platform_ids && avail.platform_ids.length > 0) {
            const { data: platforms, error: platformsError } = await supabase
              .from('streaming_platforms')
              .select('*')
              .in('id', avail.platform_ids);

            if (platformsError) {
              console.error('Error fetching platforms:', platformsError);
              return { ...avail, streaming_platforms: [] };
            }

            return { ...avail, streaming_platforms: platforms || [] };
          }
          return { ...avail, streaming_platforms: [] };
        })
      );

      return processedData;
    },
    CACHE_TTL.COUNTRY_AVAILABILITY,
    'stale-while-revalidate' // Good balance for availability data
  );
}

// User preferences functions
export async function getUserPreferences(): Promise<UserPreferences | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data, error } = await supabase
      .from('user_preferences')
      .select('*')
      .eq('user_id', user.id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return null;
      }
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error fetching user preferences:', error);
    return null;
  }
}

export async function saveUserCountryPreference(countryCode: string): Promise<void> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.log('No authenticated user, skipping country preference save');
      return;
    }

    // Validate country code
    if (!/^[A-Z]{2}$/.test(countryCode)) {
      throw new Error('Invalid country code format');
    }

    const { error } = await supabase
      .from('user_preferences')
      .upsert({
        user_id: user.id,
        last_selected_country: countryCode,
        updated_at: new Date().toISOString()
      }, {
        onConflict: 'user_id'
      });

    if (error) {
      throw error;
    }

    //console.log('Successfully saved country preference:', countryCode);
  } catch (error) {
    console.error('Error saving user country preference:', error);
    // Don't throw - this should be a non-blocking operation
  }
}

export async function addToUserSearchHistory(contentId: string): Promise<void> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.log('No authenticated user, skipping search history save');
      return;
    }

    // Validate content ID format
    if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(contentId)) {
      throw new Error('Invalid content ID format');
    }

    // Get current preferences
    const { data: currentPrefs } = await supabase
      .from('user_preferences')
      .select('searched_content_ids')
      .eq('user_id', user.id)
      .single();

    let searchedIds = currentPrefs?.searched_content_ids || [];

    // Remove if already exists (to move to front)
    searchedIds = searchedIds.filter(id => id !== contentId);

    // Add to front of array
    searchedIds.unshift(contentId);

    // Limit to last 50 searches
    if (searchedIds.length > 50) {
      searchedIds = searchedIds.slice(0, 50);
    }

    // Update preferences
    const { error } = await supabase
      .from('user_preferences')
      .upsert({
        user_id: user.id,
        searched_content_ids: searchedIds,
        updated_at: new Date().toISOString()
      }, {
        onConflict: 'user_id'
      });

    if (error) {
      throw error;
    }

    //console.log('Successfully added to search history:', contentId);

    // Invalidate search history cache immediately for all countries since user data changed
    const currentCountry = localStorage.getItem('selectedCountry') || 'US';

    // Import and use the invalidation function
    const { invalidateUserSearchHistoryCache } = await import('./cache');
    invalidateUserSearchHistoryCache(currentCountry);

    // Also invalidate the enhanced cache
    const { EnhancedCache } = await import('./enhanced-cache');
    await EnhancedCache.delete(CACHE_KEYS.USER_SEARCH_HISTORY(currentCountry));

    //console.log('🗑️ Invalidated search history cache after adding:', contentId);
  } catch (error) {
    console.error('Error saving to search history:', error);
    // Don't throw - this should be a non-blocking operation
  }
}

export async function getUserSearchHistory(country: string, limit: number = 10): Promise<Content[]> {
  return EnhancedCache.get(
    CACHE_KEYS.USER_SEARCH_HISTORY(country),
    async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          return [];
        }

        // Get user's searched content IDs
        const { data: preferences } = await supabase
          .from('user_preferences')
          .select('searched_content_ids')
          .eq('user_id', user.id)
          .single();

        if (!preferences?.searched_content_ids || preferences.searched_content_ids.length === 0) {
          return [];
        }

        // Get content details for searched IDs (maintain order)
        const searchedIds = preferences.searched_content_ids.slice(0, limit);

        const { data: searchedContent, error } = await supabase
          .from('content')
          .select(`
            *,
            availability:content_availability!inner(
              *,
              streaming_platforms(*)
            )
          `)
          .eq('availability.country_code', country)
          .in('id', searchedIds);

        if (error) {
          console.error('Error fetching search history:', error);
          return [];
        }

        // Maintain the order from the searched_content_ids array
        const orderedContent = searchedIds
          .map(id => searchedContent?.find(item => item.id === id))
          .filter(Boolean) as Content[];

        //console.log('Retrieved search history:', {
        //  userId: user.id,
        //  totalSearched: preferences.searched_content_ids.length,
        //  returned: orderedContent.length
        //});

        return orderedContent;
      } catch (error) {
        console.error('Error getting search history:', error);
        return [];
      }
    },
    CACHE_TTL.USER_SEARCH_HISTORY,
    'stale-while-revalidate' // Use stale-while-revalidate for better UX
  );
}

export async function initializeUserCountryPreference(): Promise<string> {
  try {
    // First check if user has saved preferences
    const preferences = await getUserPreferences();
    if (preferences?.last_selected_country) {
      //console.log('Found saved country preference:', preferences.last_selected_country);
      return preferences.last_selected_country;
    }

    // Fall back to localStorage
    const storedCountry = localStorage.getItem('selectedCountry');
    if (storedCountry) {
      //console.log('Found localStorage country:', storedCountry);
      // Save to user preferences for future use
      await saveUserCountryPreference(storedCountry);
      return storedCountry;
    }

    // Default to US
    logger.info('No saved country found, defaulting to US');
    await saveUserCountryPreference('US');
    return 'US';
  } catch (error) {
    logger.error('Error initializing user country preference:', error);
    return localStorage.getItem('selectedCountry') || 'US';
  }
}